import styles from './index.module.css';

/**
 * Highlights every occurrence of `query` within already-sanitized HTML by
 * wrapping matching text in a <mark>, without disturbing the surrounding
 * markup. Unlike string-replace based highlighting, this only ever touches
 * text nodes, so it can't split or corrupt tags.
 *
 * A match that is split across two sibling text nodes (e.g. "lo" bolded,
 * "go" not) will not be found, mirroring the same limitation the plain-text
 * `highlightSearchText` already has.
 */
export function highlightSearchTextInHtml(
  html: string,
  query?: string
): string {
  const normalizedQuery = query?.trim();
  if (!normalizedQuery || typeof document === 'undefined') return html;

  const container = document.createElement('div');
  container.innerHTML = html;

  const lowerQuery = normalizedQuery.toLowerCase();
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    textNodes.push(node as Text);
  }

  textNodes.forEach(textNode => {
    const text = textNode.textContent || '';
    const lowerText = text.toLowerCase();
    if (!lowerText.includes(lowerQuery)) return;

    const fragment = document.createDocumentFragment();
    let cursor = 0;
    let matchIndex = lowerText.indexOf(lowerQuery, cursor);
    while (matchIndex !== -1) {
      if (matchIndex > cursor) {
        fragment.appendChild(
          document.createTextNode(text.slice(cursor, matchIndex))
        );
      }
      const mark = document.createElement('mark');
      mark.className = styles.searchMatch;
      mark.textContent = text.slice(
        matchIndex,
        matchIndex + normalizedQuery.length
      );
      fragment.appendChild(mark);
      cursor = matchIndex + normalizedQuery.length;
      matchIndex = lowerText.indexOf(lowerQuery, cursor);
    }
    if (cursor < text.length) {
      fragment.appendChild(document.createTextNode(text.slice(cursor)));
    }

    if (textNode.parentNode) {
      textNode.parentNode.replaceChild(fragment, textNode);
    }
  });

  return container.innerHTML;
}
